/**
 * idealizedCirculation.ts
 *
 * Provides an IDEALIZED, latitude/pressure-dependent approximation of global
 * atmospheric circulation (a simplified three-cell model: Hadley, Ferrel and
 * Polar cells, plus mid-latitude jet streams and tropical trade winds).
 *
 * This is a well-known textbook simplification of real atmospheric dynamics,
 * used as a PLACEHOLDER for live NOAA GFS wind fields (UGRD/VGRD/DZDT) which
 * will replace it once real data ingestion is implemented. Unlike the noise
 * module, every shape used here corresponds to an actual named, documented
 * circulation feature, so the resulting animation is a reasonable physical
 * sketch of the real atmosphere rather than arbitrary motion.
 *
 * Vertical velocities are deliberately kept at realistic synoptic-scale
 * magnitudes (order 1-10 cm/s); the UI's "vertical motion exaggeration"
 * control is what makes them visible, exactly as it will need to do for
 * real DZDT data later.
 */

import { pressureToTemperatureK } from "./standardAtmosphere";

export interface WindSample {
  /** Zonal (eastward positive) wind, m/s. */
  uMs: number;
  /** Meridional (northward positive) wind, m/s. */
  vMs: number;
  /** Geometric vertical velocity (upward positive), m/s. */
  wMs: number;
  /** Air temperature, Kelvin. */
  temperatureK: number;
}

const DEG2RAD = Math.PI / 180;

/** Three-cell meridional overturning boundaries, degrees from the equator. */
const CELL_BOUNDARIES_DEG = [0, 30, 60, 90];
/** Sign of vertical motion (rising = +1) at the start of each cell: Hadley, Ferrel, Polar. */
const CELL_START_SIGN = [1, -1, 1];

/** Returns the vertical-motion sign/shape (unitless, roughly [-1, 1]) for the overturning cells. */
function verticalCellPattern(latAbsDeg: number): number {
  const clamped = Math.min(latAbsDeg, 90);
  for (let i = 0; i < CELL_START_SIGN.length; i++) {
    const start = CELL_BOUNDARIES_DEG[i];
    const end = CELL_BOUNDARIES_DEG[i + 1];
    if (clamped >= start && clamped <= end) {
      const f = (clamped - start) / (end - start);
      return CELL_START_SIGN[i] * Math.cos(f * Math.PI);
    }
  }
  return 0;
}

/** Height envelope (0..1-ish) for vertical motion: strongest mid-troposphere, weak at surface/tropopause. */
function verticalHeightEnvelope(pressureHpa: number): number {
  const tropopauseHpa = 150;
  if (pressureHpa >= tropopauseHpa) {
    const t = clamp(
      (Math.log(1000) - Math.log(pressureHpa)) / (Math.log(1000) - Math.log(tropopauseHpa)),
      0,
      1
    );
    return Math.sin(Math.PI * t);
  }
  // Weak residual stratospheric overturning (e.g. Brewer-Dobson circulation), much smaller.
  const decay = Math.exp(-Math.log(tropopauseHpa / pressureHpa) * 0.6);
  return decay * 0.15;
}

/** Mid-latitude jet streams + tropical trade easterlies, as a function of |latitude|. */
function zonalWindShape(latAbsDeg: number): number {
  const subtropicalJet = 34 * gaussian(latAbsDeg, 30, 9);
  const polarJet = 28 * gaussian(latAbsDeg, 58, 11);
  const tradeEasterlies = -7 * gaussian(latAbsDeg, 0, 16);
  return subtropicalJet + polarJet + tradeEasterlies;
}

/** Height scaling for zonal wind: peaks near the tropopause jet core (~250 hPa). */
function zonalHeightScale(pressureHpa: number): number {
  const logP = Math.log(pressureHpa);
  const logJetCore = Math.log(250);
  const jetPeak = Math.exp(-((logP - logJetCore) ** 2) / (2 * 0.85 ** 2));
  return 0.15 + 0.85 * jetPeak;
}

function gaussian(x: number, mean: number, sigma: number): number {
  return Math.exp(-((x - mean) ** 2) / (2 * sigma * sigma));
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Equator-to-pole surface temperature contrast that fades with altitude. */
function latitudeTemperatureAnomalyK(latAbsDeg: number, pressureHpa: number): number {
  const poleEquatorContrastK = 45;
  const surfaceWeight = clamp((pressureHpa - 200) / (1000 - 200), 0, 1);
  return -poleEquatorContrastK * (latAbsDeg / 90) ** 2 * surfaceWeight;
}

/**
 * Samples the idealized circulation at a given longitude/latitude (degrees)
 * and pressure level (hPa). The idealized three-cell model is zonally
 * symmetric by construction, so longitude is currently unused; the
 * parameter is kept so this function's signature already matches what a
 * real per-cell GFS wind sampler will need.
 */
export function sampleIdealizedWind(_lonDeg: number, latDeg: number, pressureHpa: number): WindSample {
  const latAbsDeg = Math.abs(latDeg);
  const hemisphereSign = latDeg >= 0 ? 1 : -1;

  const uMs = zonalWindShape(latAbsDeg) * zonalHeightScale(pressureHpa);

  const cellPattern = verticalCellPattern(latAbsDeg);
  const heightEnvelope = verticalHeightEnvelope(pressureHpa);
  const W_SCALE = 0.05; // m/s, realistic large-scale vertical velocity magnitude
  const wMs = cellPattern * heightEnvelope * W_SCALE;

  // Meridional flow approximated from mass continuity: poleward aloft, equatorward near
  // the surface, strongest near cell mid-points (quarter-phase shifted from vertical motion).
  const levelFactor = clamp((500 - pressureHpa) / 500, -1, 1); // + aloft, - near surface
  const V_SCALE = 4.5; // m/s
  const vMs = hemisphereSign * cellPattern * levelFactor * V_SCALE * Math.sin(latAbsDeg * DEG2RAD);

  const temperatureK =
    pressureToTemperatureK(pressureHpa) + latitudeTemperatureAnomalyK(latAbsDeg, pressureHpa);

  return { uMs, vMs, wMs, temperatureK };
}
